import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Button from "@/components/ui/button";
import Switch from "@/components/ui/switch";
import { Bell } from "@/components/icons/";

import { notificationsApi } from "../services/notifications.api";
import {
  NotificationChannel,
  type NotificationPreference,
} from "../types/notifications.types";

import styles from "./NotificationPreferencesPage.module.scss";

/**
 * Notification preferences: one switch per channel (EMAIL / SMS / PUSH).
 *
 * Rationale: backend preference is modelled per channel only (no per-type
 * granularity). A richer per-category UI would require a schema change, so
 * we keep the UI faithful to what the server actually supports.
 */
const CHANNEL_META: Record<
  number,
  { labelKey: string; label: string; descKey: string; desc: string }
> = {
  [NotificationChannel.EMAIL]: {
    labelKey: "notifications.channel.email",
    label: "E-posta",
    descKey: "notifications.channel.emailDesc",
    desc: "Sistem bildirimlerini e-posta ile al",
  },
  [NotificationChannel.SMS]: {
    labelKey: "notifications.channel.sms",
    label: "SMS",
    descKey: "notifications.channel.smsDesc",
    desc: "Kritik bildirimleri SMS ile al",
  },
  [NotificationChannel.PUSH]: {
    labelKey: "notifications.channel.push",
    label: "Push",
    descKey: "notifications.channel.pushDesc",
    desc: "Mobil uygulama bildirimleri",
  },
};

const NotificationPreferencesPage: React.FC = () => {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fetched = await notificationsApi.getNotificationPreferences();
        if (cancelled) return;
        const byChannel = new Map<number, NotificationPreference>();
        fetched.forEach((p) => byChannel.set(Number(p.channel), p));
        // Ensure all three channels are represented in UI order.
        const hydrated = [
          NotificationChannel.EMAIL,
          NotificationChannel.SMS,
          NotificationChannel.PUSH,
        ].map<NotificationPreference>(
          (ch) => byChannel.get(ch) ?? { channel: ch, enabled: true },
        );
        setPrefs(hydrated);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggle = (channel: number, enabled: boolean) => {
    setPrefs((list) =>
      list.map((p) =>
        Number(p.channel) === channel ? { ...p, enabled } : p,
      ),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await notificationsApi.updateNotificationPreferences(prefs);
      toast.success(t("common.successfullySaved"));
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo(() => prefs, [prefs]);

  return (
    <DashboardLayout>
      <div className={styles.page}>
      <Header
        title={t("notifications.preferencesTitle")}
        description={t("notifications.preferencesDescription")}
        icon={<Bell width={24} height={24} />}
      />

      <div className={styles.card}>
        {loading ? (
          <div className={styles.loading}>
            {t("common.loading")}
          </div>
        ) : (
          rows.map((p) => {
            const meta = CHANNEL_META[Number(p.channel)];
            return (
              <div key={p.channel} className={styles.row}>
                <div className={styles.rowInfo}>
                  <span className={styles.rowTitle}>
                    {t(meta.labelKey, meta.label)}
                  </span>
                  <span className={styles.rowDescription}>
                    {t(meta.descKey, meta.desc)}
                  </span>
                </div>
                {/* `key` forces remount on channel change so Switch internal state aligns with server value. */}
                <Switch
                  key={`${p.channel}-${p.enabled}`}
                  checked={p.enabled}
                  onChange={(next) => handleToggle(Number(p.channel), next)}
                />
              </div>
            );
          })
        )}
      </div>

      <div className={styles.footer}>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          onClick={handleSave}
          loading={saving}
          disabled={saving || loading}
        >
          {t("common.save")}
        </Button>
      </div>
      </div>
    </DashboardLayout>
  );
};

export default NotificationPreferencesPage;
